import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Send, Search, Loader2, MessageSquare, ArrowLeft } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { useNotificationStore } from '../../stores/notificationStore'
import { messagingService } from '../../services/messagingService'
import type { Conversation, ConversationDetail, Message } from '../../types/messaging'
import toast from 'react-hot-toast'

export default function MessagesPage() {
  const { conversationId } = useParams<{ conversationId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { fetchCounts } = useNotificationStore()

  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeConversation, setActiveConversation] = useState<ConversationDetail | null>(null)
  const [isLoadingList, setIsLoadingList] = useState(true)
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [message, setMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Load conversations list
  useEffect(() => {
    const loadConversations = async () => {
      try {
        setIsLoadingList(true)
        const response = await messagingService.getConversations()
        setConversations(response.results || [])
      } catch (err) {
        console.error('Error loading conversations:', err)
        toast.error('Failed to load conversations')
      } finally {
        setIsLoadingList(false)
      }
    }

    loadConversations()
  }, [])

  const loadConversation = useCallback(async (id: string) => {
    try {
      setIsLoadingMessages(true)
      const data = await messagingService.getConversation(id)
      setActiveConversation(data)
      // Update unread count in list
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c))
      )
      // Refresh sidebar badge
      fetchCounts()
    } catch (err) {
      console.error('Error loading conversation:', err)
      toast.error('Failed to load conversation')
    } finally {
      setIsLoadingMessages(false)
    }
  }, [fetchCounts])

  // Load active conversation when ID changes
  useEffect(() => {
    if (conversationId) {
      loadConversation(conversationId)
    } else {
      setActiveConversation(null)
    }
  }, [conversationId, loadConversation])

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [activeConversation?.messages])

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activeConversation || !message.trim()) return

    try {
      setIsSending(true)
      const newMessage = await messagingService.sendMessage(activeConversation.id, message.trim())

      // Add message to list
      setActiveConversation((prev) =>
        prev ? { ...prev, messages: [...prev.messages, newMessage] } : null
      )

      // Update conversation preview in list
      setConversations((prev) =>
        prev.map((c) =>
          c.id === activeConversation.id
            ? {
                ...c,
                last_message: newMessage,
                last_message_preview: newMessage.content.slice(0, 200),
                last_message_at: newMessage.created_at,
              }
            : c
        )
      )

      setMessage('')
    } catch (err) {
      console.error('Error sending message:', err)
      toast.error('Failed to send message')
    } finally {
      setIsSending(false)
    }
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    } else if (days === 1) {
      return 'Yesterday'
    } else if (days < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' })
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const filteredConversations = conversations.filter((c) => {
    if (!searchQuery) return true
    const name = c.other_participant?.display_name || ''
    return name.toLowerCase().includes(searchQuery.toLowerCase())
  })

  const isOwnMessage = (msg: Message) => msg.sender.id === user?.id

  return (
    <div className="h-[calc(100vh-8rem)]">
      <div className="grid lg:grid-cols-3 gap-6 h-full">
        {/* Conversation List */}
        <div className={`card flex flex-col ${conversationId ? 'hidden lg:flex' : ''}`}>
          <div className="p-4 border-b border-secondary-200">
            <h2 className="font-semibold mb-3">Messages</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoadingList ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-secondary-500 text-sm p-4">
                <MessageSquare className="h-8 w-8 mb-2 text-secondary-300" />
                <p>No conversations yet</p>
                <p className="text-xs mt-1 text-center">
                  After you pay for a service, you can message the expert here.
                </p>
              </div>
            ) : (
              filteredConversations.map((conversation) => (
                <button
                  key={conversation.id}
                  onClick={() => navigate(`/messages/${conversation.id}`)}
                  className={`w-full p-4 hover:bg-secondary-50 border-b border-secondary-100 text-left transition-colors ${
                    conversationId === conversation.id ? 'bg-primary-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {conversation.other_participant?.profile_picture_url ? (
                      <img
                        src={conversation.other_participant.profile_picture_url}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-medium flex-shrink-0">
                        {conversation.other_participant?.display_name?.slice(0, 2)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium truncate">
                          {conversation.other_participant?.type === 'talent' && conversation.other_participant?.profile_id ? (
                            <Link
                              to={`/talents/${conversation.other_participant.profile_id}`}
                              className="hover:text-primary-600"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {conversation.other_participant.display_name}
                            </Link>
                          ) : (
                            conversation.other_participant?.display_name || 'Unknown'
                          )}
                        </h3>
                        {conversation.last_message_at && (
                          <span className="text-xs text-secondary-500">
                            {formatTime(conversation.last_message_at)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-secondary-500 truncate">
                          {conversation.last_message_preview || 'No messages yet'}
                        </p>
                        {conversation.unread_count > 0 && (
                          <span className="ml-2 bg-primary-600 text-white text-xs rounded-full px-2 py-0.5">
                            {conversation.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Window */}
        <div className={`lg:col-span-2 card flex flex-col ${!conversationId ? 'hidden lg:flex' : ''}`}>
          {activeConversation ? (
            <>
              {/* Chat header */}
              <div className="p-4 border-b border-secondary-200 flex items-center gap-3">
                <button
                  onClick={() => navigate('/messages')}
                  className="lg:hidden p-2 hover:bg-secondary-100 rounded-lg"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                {activeConversation.other_participant?.profile_picture_url ? (
                  <img
                    src={activeConversation.other_participant.profile_picture_url}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-medium">
                    {activeConversation.other_participant?.display_name?.slice(0, 2)?.toUpperCase() || '?'}
                  </div>
                )}
                <div>
                  <h3 className="font-medium">
                    {activeConversation.other_participant?.type === 'talent' && activeConversation.other_participant?.profile_id ? (
                      <Link
                        to={`/talents/${activeConversation.other_participant.profile_id}`}
                        className="hover:text-primary-600"
                      >
                        {activeConversation.other_participant.display_name}
                      </Link>
                    ) : (
                      activeConversation.other_participant?.display_name || 'Unknown'
                    )}
                  </h3>
                  <p className="text-xs text-secondary-500">
                    {activeConversation.other_participant?.headline || ''}
                  </p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {isLoadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                  </div>
                ) : activeConversation.messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-secondary-500 text-sm">
                    <MessageSquare className="h-8 w-8 mb-2 text-secondary-300" />
                    <p>No messages yet</p>
                    <p className="text-xs mt-1">Start the conversation!</p>
                  </div>
                ) : (
                  <>
                    {activeConversation.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${isOwnMessage(msg) ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`rounded-lg px-4 py-2 max-w-[70%] ${
                            isOwnMessage(msg)
                              ? 'bg-primary-600 text-white'
                              : 'bg-secondary-100'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <span
                            className={`text-xs ${
                              isOwnMessage(msg) ? 'text-primary-200' : 'text-secondary-500'
                            }`}
                          >
                            {formatTime(msg.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input */}
              <div className="p-4 border-t border-secondary-200">
                <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="input flex-1"
                    disabled={isSending}
                  />
                  <button
                    type="submit"
                    disabled={isSending || !message.trim()}
                    className="btn btn-primary"
                  >
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-secondary-500">
              <MessageSquare className="h-12 w-12 mb-3 text-secondary-300" />
              <p className="font-medium">Select a conversation</p>
              <p className="text-sm mt-1">Choose a conversation from the list to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
